<template>
  <div v-if="teaserUrl">
    <ProductTeaser
      :title="title"
      :image="media"
      :url="teaserUrl"
      :status="teaserStatus"
    />
    <div v-if="variations.length" class="flex justify-center">
      <template v-if="colorChoices.length > 1">
        <div
          v-for="item in colorChoices"
          :key="item.label"
          @mouseover="hoverVariation = item.id"
        >
          <a
            :href="item.url"
            :title="item.label"
            class="mx-5 mt-10 pill"
            :style="{
              backgroundColor: '#' + item.hex,
            }"
            @click.prevent="onClickColor(item.url)"
          />
        </div>
      </template>
      <div
        v-for="variation in zipperChoices"
        :key="variation.id"
        class="zipper flex mt-10"
        @mouseover="hoverVariation = variation.id"
      >
        <a
          v-if="variation.zipper && variation.zipper[0]"
          :href="url + '?sku=' + variation.sku"
          :title="variation.zipper[0].label"
          class="text-xl font-semibold relative leading-none text-transparent px-10 inline-block pt-3"
        >
          <span class="relative" :data-label="variation.zipper[0]">{{
            variation.zipper[0]
          }}</span>
        </a>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type {
  ApiSearchProductResultItemVariation,
  ProductVariationDocument,
} from '@search/types'
import type { MediaImageFragment } from '#graphql-operations'
import { falsy } from '~/helpers/type'

const router = useRouter()

interface VariationMatchScoring {
  score: number
  variation: ApiSearchProductResultItemVariation
}

type VariationKey = keyof ProductVariationDocument

export interface ActiveTermFilter {
  field: VariationKey
  terms: number[]
}

const props = withDefaults(
  defineProps<{
    title?: string
    id?: string
    url?: string
    variations?: ApiSearchProductResultItemVariation[]
    termFilters?: ActiveTermFilter[]
  }>(),
  {
    variations: () => [],
    termFilters: () => [],
    title: undefined,
    id: undefined,
    url: undefined,
  },
)

const hoverVariation = ref<string | null>(null)

const activeVariation = computed(() => {
  if (!props.variations) {
    return
  }
  if (hoverVariation.value) {
    const match = props.variations.find((v) => v.id === hoverVariation.value)
    if (match) {
      return match
    }
  }

  // Return the default variation if no filters are active.
  if (!props.termFilters?.length) {
    return (
      props.variations?.find((v) => v.is_default_variation) ||
      props.variations?.[0]
    )
  }

  // Get the highest scored variation.
  const sorted = props.variations
    .filter((v) => v.published)
    .map(getVariationScore)
    .sort((a, b) => {
      return a.score - b.score
    })
    .reverse()

  return sorted[0].variation
})

/**
 * Since this is data is from the search index, we have to mock this in order
 * to use the MediaImage component. Only the rokka hash is indexed.
 */
const media = computed<MediaImageFragment | undefined>(() => {
  if (activeVariation.value?.rokka_hash) {
    return {
      image: {
        file: {
          label: activeVariation.value?.sku + '.jpg',
          rokka: {
            hash: activeVariation.value.rokka_hash,
          },
        },
      },
    }
  }

  return undefined
})

type ColorChoice = {
  id: string
  hex: string
  label: string
  url: string
}

const colorChoices = computed<ColorChoice[]>(() => {
  return props.variations
    .filter((v) => v.published)
    .reverse()
    .map(getVariationScore)
    .sort((a, b) => b.score - a.score)
    .reverse()
    .map((v) => {
      const color = v.variation.colorMapped[0]
      if (!color?.hex) {
        return
      }
      return {
        id: v.variation.id,
        hex: color.hex,
        label: color.label,
        url: props.url + '?sku=' + v.variation.sku,
      }
    })
    .filter(falsy)
    .filter(
      (item, index, self) =>
        self.findIndex((v) => v.label === item.label) === index,
    )
})

const zipperChoices = computed(() => {
  const choices: ProductVariationDocument[] = []
  if (
    props.variations &&
    props.variations[0].zipper &&
    props.variations[0].zipper[0]
  ) {
    const sorted = props.variations
      .filter((v) => v.published)
      .map(getVariationScore)
      .sort((a, b) => {
        return a.score - b.score
      })
      .reverse()
    sorted.forEach((v) => {
      const includesChoice = choices.some((c) => {
        return c.zipper ? c.zipper[0] === v.variation.zipper[0] : false
      })
      if (!includesChoice) {
        choices.push(v.variation)
      }
    })
    if (choices.length > 1) {
      return choices
    }
  }
  return null
})

/**
 * Build the URL.
 */
const teaserUrl = computed(() => {
  return activeVariation.value
    ? props.url + '?sku=' + activeVariation.value.sku
    : props.url
})

const teaserStatus = computed(() => {
  return (
    activeVariation.value?.product_status.map((label) => {
      return label.toString()
    }) || []
  )
})

/**
 * Calculate the matching score of a variation.
 *
 * The score is calculated based on how many filter terms it matches.
 */
function getVariationScore(
  variation: ApiSearchProductResultItemVariation,
): VariationMatchScoring {
  let score = 0
  props.termFilters?.forEach((filter) => {
    if (filter.field === 'color') {
      const values = variation.color_group || []
      if (Array.isArray(values)) {
        filter.terms.forEach((term) => {
          if (values.includes(term.toString())) {
            score++
          }
        })
      }
    } else {
      const values = variation[filter.field] || []
      if (Array.isArray(values)) {
        filter.terms.forEach((term) => {
          if (values.includes(term)) {
            score++
          }
        })
      }
    }
    if (!variation.published) {
      score = 0
    }
  })

  return { score, variation }
}
const onClickColor = (url: string) => {
  router.push(url)
}
</script>

<style lang="postcss" scoped>
.pill:hover {
  @apply ring ring-inset ring-black;
}
.zipper {
  &:not(:last-child) {
    @apply border-r-1;
  }
  a {
    span:before {
      content: attr(data-label);
      @apply font-light absolute top-0 w-full h-full text-center select-none pointer-events-none text-black;
    }
    &:hover {
      @apply text-black;
      span:before {
        @apply text-transparent;
      }
    }
  }
}
</style>
